import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Archive, Download, FileText, Lock, Pencil, Users } from 'lucide-react';

import {
  deleteDocument,
  getDocument,
  getDocumentDownloadUrl,
  updateDocument,
  type DocumentDetail,
  type DocumentGrantView,
  type GrantInput,
} from '@/api/documents.api';
import { GrantsEditor } from './DocumentsPage';
import { listDepartments, listEmployees } from '@/api/employees.api';
import { listSeats } from '@/api/seats.api';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';

/**
 * `/documents/:id` — one document's metadata + download, plus (for managers,
 * per `detail.canManage`) an access panel that lists who can see it with edit
 * (PUT) and archive (DELETE) actions. ADR-035.
 */
export function DocumentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();

  const {
    data: doc,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['documents', id],
    queryFn: () => getDocument(id ?? ''),
    enabled: Boolean(id),
  });

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const downloadMutation = useMutation({
    mutationFn: async () => {
      if (!doc) return;
      const dl = await getDocumentDownloadUrl(doc.id);
      window.open(dl.url, '_blank', 'noopener,noreferrer');
    },
    onError: (err) => {
      toast.show(describeDocError(err, 'Could not open the document.'), { variant: 'danger' });
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
      </div>
    );
  }

  if (error || !doc) {
    return (
      <Card className="mx-auto flex max-w-2xl items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
        <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
        <div className="flex-1">
          <p className="text-[14px] font-medium text-kw-danger-text">Document not available</p>
          <p className="mt-1 text-[13px] text-kw-danger-text/80">
            {error instanceof Error
              ? describeDocError(error, 'This document does not exist or you do not have access to it.')
              : 'This document does not exist or you do not have access to it.'}
          </p>
          <Link to="/documents" className="mt-3 inline-block text-[13px] text-kw-cerulean hover:underline">
            ← Back to all documents
          </Link>
        </div>
      </Card>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <Link
          to="/documents"
          className="inline-flex items-center gap-1 text-[13px] text-kw-muted hover:text-kw-shark"
        >
          <ArrowLeft className="size-4" />
          All documents
        </Link>
      </div>

      <header className="space-y-2 border-b border-kw-border pb-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="info">{doc.category}</Badge>
          {doc.canManage ? (
            <button
              type="button"
              onClick={() => {
                setDetailsOpen(true);
              }}
              className="inline-flex items-center gap-1 text-[12px] text-kw-cerulean hover:text-kw-cerulean-dark"
            >
              <Pencil className="size-3.5" />
              Edit details
            </button>
          ) : null}
        </div>
        <h1 className="typo-h1">{doc.title}</h1>
        {doc.description ? <p className="text-[14px] text-kw-shark">{doc.description}</p> : null}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-kw-muted">
          <span>Uploaded by {doc.uploaderNameSnapshot}</span>
          <span>Updated {new Date(doc.updatedAt).toLocaleDateString()}</span>
        </div>
      </header>

      {/* File + download */}
      <Card className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <div className="grid size-10 shrink-0 place-items-center rounded-kw-md bg-kw-bg-blue text-kw-cerulean-dark">
            <FileText className="size-5" />
          </div>
          <div className="min-w-0">
            <p className="truncate text-[14px] font-medium text-kw-shark">
              {doc.filename ?? 'No file attached'}
            </p>
            <p className="text-[12px] text-kw-muted">
              {doc.contentType ?? 'unknown type'}
              {doc.sizeBytes != null ? ` · ${(doc.sizeBytes / 1024).toFixed(0)} KB` : ''}
            </p>
          </div>
        </div>
        <Button
          isLoading={downloadMutation.isPending}
          disabled={!doc.fileObjectId}
          onClick={() => {
            downloadMutation.mutate();
          }}
        >
          <Download className="size-4" />
          Download
        </Button>
      </Card>

      {/* Manager access panel */}
      {doc.canManage ? <AccessPanel doc={doc} /> : null}

      {/* Manager archive */}
      {doc.canManage ? (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            onClick={() => {
              setArchiveOpen(true);
            }}
          >
            <Archive className="size-4" />
            Archive document
          </Button>
        </div>
      ) : null}

      {doc.canManage ? (
        <EditDetailsModal
          doc={doc}
          open={detailsOpen}
          onClose={() => {
            setDetailsOpen(false);
          }}
        />
      ) : null}
      {doc.canManage ? (
        <ArchiveModal
          doc={doc}
          open={archiveOpen}
          onClose={() => {
            setArchiveOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

// ── Local helpers (kept here so DocumentsPage stays components-only) ───────

function formatRole(role: string): string {
  return role
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function errData(err: unknown): { error?: string; message?: string } | undefined {
  if (err && typeof err === 'object' && 'response' in err) {
    return (err as { response?: { data?: { error?: string; message?: string } } }).response?.data;
  }
  return undefined;
}

function describeDocError(err: unknown, fallback: string): string {
  const data = errData(err);
  switch (data?.error) {
    case 'storage_disabled':
      return 'File storage isn’t configured, so the document can’t be served.';
    case 'document_not_found':
      return 'That document is no longer available.';
    case 'file_not_found':
      return 'The attached file could not be found.';
    case 'org_not_permitted':
      return 'You can only share documents with organizations you belong to.';
    case 'invalid_file':
      return 'The uploaded file is not ready — try uploading it again.';
    case 'invalid_grant':
      return 'One of the access grants points at something that no longer exists.';
    case 'validation_failed':
      return 'Some fields are missing or invalid.';
    default:
      if (typeof data?.message === 'string') return data.message;
      return err instanceof Error ? err.message : fallback;
  }
}

// ── Read-only grant label from the resolved view ──────────────────────────

function describeGrantView(g: DocumentGrantView): string {
  switch (g.targetType) {
    case 'all':
      return 'Everyone in the org';
    case 'role':
      return `Role · ${formatRole(g.targetRole ?? '')}`;
    case 'seat':
      return `Seat · ${g.targetSeatName ?? '(removed seat)'}`;
    case 'department':
      return `Department · ${g.targetDepartmentName ?? '(removed department)'}`;
    case 'membership':
      return `Person · ${g.targetMemberName ?? '(removed member)'}`;
    default:
      return 'Access';
  }
}

// ── Manager access panel — list + edit grants ─────────────────────────────

function AccessPanel({ doc }: { doc: DocumentDetail }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { state: authState } = useAuth();
  // Author grants against the caller's CURRENT session org — never
  // doc.orgIds[0] (arbitrary DB order / the creating org): the seat/
  // department/person pickers are scoped to the current org server-side, so a
  // grant must carry that same org id (also enforced by the server).
  const currentOrgId = authState.status === 'authenticated' ? authState.me.membership.organizationId : '';
  const [editing, setEditing] = useState(false);
  const [grants, setGrants] = useState<GrantInput[]>([]);

  const seatsQuery = useQuery({
    queryKey: ['documents', 'wizard', 'seats'],
    queryFn: () => listSeats({ scenario: 'current' }),
    enabled: editing,
  });
  const departmentsQuery = useQuery({
    queryKey: ['documents', 'wizard', 'departments'],
    queryFn: listDepartments,
    enabled: editing,
  });
  const employeesQuery = useQuery({
    queryKey: ['documents', 'wizard', 'employees'],
    queryFn: () => listEmployees({ status: 'active', limit: 200 }),
    enabled: editing,
  });

  const saveMutation = useMutation({
    mutationFn: () => updateDocument(doc.id, { grants }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast.show('Access updated.', { variant: 'success' });
      setEditing(false);
    },
    onError: (err) => {
      toast.show(describeDocError(err, 'Could not update access.'), { variant: 'danger' });
    },
  });

  function startEditing(): void {
    setGrants(doc.grants.map(grantViewToInput));
    setEditing(true);
  }

  return (
    <Card className="space-y-3 p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-[14px] font-medium text-kw-shark">
          <Users className="size-4 text-kw-muted" />
          Who can access this
        </p>
        {editing ? (
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setEditing(false);
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              isLoading={saveMutation.isPending}
              onClick={() => {
                saveMutation.mutate();
              }}
            >
              Save access
            </Button>
          </div>
        ) : (
          <Button size="sm" variant="secondary" onClick={startEditing}>
            <Pencil className="size-4" />
            Edit access
          </Button>
        )}
      </div>

      {editing ? (
        <GrantsEditor
          grants={grants}
          onAdd={(g) => {
            setGrants((prev) => [...prev, g]);
          }}
          onRemove={(idx) => {
            setGrants((prev) => prev.filter((_, i) => i !== idx));
          }}
          seats={seatsQuery.data ?? []}
          departments={departmentsQuery.data ?? []}
          employees={(employeesQuery.data?.data ?? []).filter((e) => e.membershipId)}
          currentOrgId={currentOrgId}
        />
      ) : doc.grants.length === 0 ? (
        <p className="flex items-center gap-2 text-[13px] text-kw-muted">
          <Lock className="size-4" />
          No access grants yet — only managers can see this document.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {doc.grants.map((g) => (
            <li
              key={g.id}
              className="flex items-center gap-2 rounded-kw-md border border-kw-border bg-white px-3 py-2 text-[13px] text-kw-shark"
            >
              <span className="flex-1">{describeGrantView(g)}</span>
              {g.aiSuggested ? (
                <span className="rounded-kw-full bg-kw-cerulean-light px-2 py-0.5 text-[10px] font-medium text-kw-cerulean-dark">
                  AI
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function grantViewToInput(g: DocumentGrantView): GrantInput {
  return {
    organizationId: g.organizationId,
    targetType: g.targetType,
    targetRole: g.targetRole,
    targetSeatId: g.targetSeatId,
    targetDepartmentId: g.targetDepartmentId,
    targetMembershipId: g.targetMembershipId,
    aiSuggested: g.aiSuggested,
  };
}

// ── Edit details (title / description / category) ─────────────────────────

function EditDetailsModal({
  doc,
  open,
  onClose,
}: {
  doc: DocumentDetail;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [title, setTitle] = useState(doc.title);
  const [description, setDescription] = useState(doc.description ?? '');
  const [category, setCategory] = useState(doc.category);

  const mutation = useMutation({
    mutationFn: () =>
      updateDocument(doc.id, {
        title: title.trim(),
        description: description.trim(),
        category: category.trim(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast.show('Document updated.', { variant: 'success' });
      onClose();
    },
    onError: (err) => {
      toast.show(describeDocError(err, 'Could not update the document.'), { variant: 'danger' });
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    mutation.mutate();
  }

  return (
    <Modal open={open} onClose={onClose} title="Edit document" size="md">
      <form
        onSubmit={(e) => {
          handleSubmit(e);
        }}
        className="space-y-4"
      >
        <Input
          label="Title"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
          }}
          required
        />
        <div>
          <label htmlFor="edit-doc-description" className="mb-1 block text-[13px] font-medium text-kw-shark">
            Description <span className="font-normal text-kw-muted">(optional)</span>
          </label>
          <textarea
            id="edit-doc-description"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
            }}
            rows={3}
            className="w-full rounded-kw-md border border-kw-border bg-white px-3 py-2 text-[14px] text-kw-shark placeholder:text-kw-muted focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
          />
        </div>
        <Input
          label="Category"
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
          }}
          required
        />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={mutation.isPending}
            disabled={title.trim() === '' || category.trim() === ''}
          >
            Save changes
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ── Archive confirm ───────────────────────────────────────────────────────

function ArchiveModal({ doc, open, onClose }: { doc: DocumentDetail; open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();

  const mutation = useMutation({
    mutationFn: () => deleteDocument(doc.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast.show('Document archived.', { variant: 'success' });
      onClose();
      void navigate('/documents');
    },
    onError: (err) => {
      toast.show(describeDocError(err, 'Could not archive the document.'), { variant: 'danger' });
    },
  });

  return (
    <Modal open={open} onClose={onClose} title="Archive document" size="sm">
      <div className="space-y-4">
        <p className="text-[13px] text-kw-shark">
          Archive <strong>{doc.title}</strong>? It will no longer appear in the library for anyone.
        </p>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            isLoading={mutation.isPending}
            onClick={() => {
              mutation.mutate();
            }}
          >
            Archive
          </Button>
        </div>
      </div>
    </Modal>
  );
}
